import type { SxProps, Theme } from '@mui/material/styles';
import {
  ultraWideDesktopQuery,
  wideDesktopQuery,
} from '../common/viewportBreakpoints';

/** Stats shell — readable width caps with padding that grows on wide / 4K viewports. */
export const statsShellSx: SxProps<Theme> = {
  width: '100%',
  maxWidth: {
    xs: '100%',
    sm: 640,
    md: 760,
    lg: 920,
    xl: 1040,
    xxl: 1160,
    xxxl: 1320,
  },
  mx: 'auto',
  py: { xs: 2, md: 2.5, xxl: 3, xxxl: 3.5 },
  px: { xs: 2, sm: 3, xxl: 4, xxxl: 5 },
};

export const statsTitleSx: SxProps<Theme> = {
  fontSize: {
    xs: '1.5rem',
    sm: '1.75rem',
    xxl: '2rem',
    xxxl: '2.375rem',
  },
  fontWeight: 600,
  mb: { xs: 2, xxl: 2.5 },
};

export const statsSectionStackSx: SxProps<Theme> = {
  display: 'flex',
  flexDirection: 'column',
  gap: { xs: 2, xxl: 2.5, xxxl: 3 },
};

export const statsPaperSx: SxProps<Theme> = {
  p: { xs: 2, xxl: 2.25, xxxl: 2.5 },
};

export const statsInteractivePaperSx: SxProps<Theme> = [
  statsPaperSx,
  {
    transition: 'background-color 0.15s ease',
    '&:hover': {
      bgcolor: 'action.hover',
    },
  },
];

export const statsRatingLabelSx: SxProps<Theme> = {
  fontSize: { xs: '0.6875rem', xxl: '0.75rem', xxxl: '0.8125rem' },
  letterSpacing: '0.08em',
  textTransform: 'uppercase',
  color: 'text.secondary',
};

export const statsRatingValueSx: SxProps<Theme> = {
  fontSize: { xs: '2.125rem', sm: '2.25rem', xxl: '2.5rem', xxxl: '2.875rem' },
  fontWeight: 400,
  lineHeight: 1.2,
};

export const statsCardTitleSx: SxProps<Theme> = {
  fontSize: { xs: '1rem', sm: '1.0625rem', xxl: '1.1875rem', xxxl: '1.375rem' },
  fontWeight: 500,
  mb: { xs: 1, xxl: 1.25 },
};

export const statsActivityTableSx: SxProps<Theme> = {
  '& .MuiTableCell-root': {
    fontSize: { xs: 13, sm: 14, md: 15 },
    py: { xs: 0.5, sm: 0.65 },
    px: 1,
    borderBottom: '1px solid',
    borderColor: 'divider',
    [`@media ${wideDesktopQuery}`]: {
      fontSize: 17,
      py: 0.85,
      px: 1.25,
    },
    [`@media ${ultraWideDesktopQuery}`]: {
      fontSize: 20,
      py: 1,
      px: 1.5,
    },
  },
  '& .MuiTableCell-head': {
    fontWeight: 600,
    bgcolor: 'action.hover',
  },
};

export const statsCountValueSx: SxProps<Theme> = {
  fontSize: { xs: '1.25rem', sm: '1.375rem', xxl: '1.5rem', xxxl: '1.75rem' },
  fontWeight: 500,
  lineHeight: 1.2,
};

export const statsCaptionSx: SxProps<Theme> = {
  fontSize: { xs: '0.75rem', sm: '0.8125rem', xxl: '0.875rem', xxxl: '1rem' },
  color: 'text.secondary',
  mt: { xs: 1, xxl: 1.25 },
};

export const statsBackButtonSx: SxProps<Theme> = {
  mb: { xs: 2, xxl: 2.5 },
  fontSize: { xs: '0.8125rem', xxl: '0.9375rem', xxxl: '1rem' },
};

export const statsTabsSx: SxProps<Theme> = {
  mb: { xs: 2, xxl: 2.5 },
  '& .MuiTab-root': {
    fontSize: { xs: '0.8125rem', sm: '0.875rem', xxl: '0.9375rem', xxxl: '1.0625rem' },
    minHeight: { xs: 40, xxl: 44, xxxl: 48 },
    px: { xs: 1.5, xxl: 2, xxxl: 2.5 },
  },
};

export const statsDetailSummarySx: SxProps<Theme> = {
  fontSize: { xs: '0.875rem', xxl: '0.9375rem', xxxl: '1.0625rem' },
  color: 'text.secondary',
  mb: { xs: 2, xxl: 2.5 },
};

export const statsInteractionPrimarySx: SxProps<Theme> = {
  fontSize: { xs: '0.875rem', xxl: '0.9375rem', xxxl: '1.0625rem' },
};

export const statsInteractionSecondarySx: SxProps<Theme> = {
  fontSize: { xs: '0.75rem', xxl: '0.8125rem', xxxl: '0.9375rem' },
};

export const statsInteractionRowSx: SxProps<Theme> = {
  py: { xs: 0.75, xxl: 0.9, xxxl: 1.1 },
  px: 0,
  borderRadius: 1,
};
